/**
 * RowLink
 *
 * A real `<a href>` (via React Router's Link) for a list row's primary
 * cell — the invoice number, family name, etc. Rendering an actual anchor
 * is what lets the browser offer right-click "Open in new tab", Ctrl/Cmd
 * click, and middle-click, and makes the row keyboard-focusable.
 *
 * It is styled to inherit the surrounding text (no color/weight change),
 * underlining only on hover, so it reads as plain text until interacted
 * with. `stopPropagation` keeps the parent row's click handler from
 * firing a second navigation when the link itself is clicked.
 */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link as MuiLink } from "@mui/material";
import type { LinkProps as MuiLinkProps } from "@mui/material";

export interface RowLinkProps extends Omit<MuiLinkProps, "href" | "component"> {
  /** Target route (e.g. `/invoices/123`) */
  to: string;
  children: React.ReactNode;
}

export const RowLink: React.FC<RowLinkProps> = ({ to, children, sx, ...rest }) => (
  <MuiLink
    component={RouterLink}
    to={to}
    onClick={(e) => e.stopPropagation()}
    onAuxClick={(e) => e.stopPropagation()}
    underline="hover"
    color="inherit"
    sx={{ fontWeight: "inherit", cursor: "pointer", ...sx }}
    {...rest}
  >
    {children}
  </MuiLink>
);

export default RowLink;
